import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import ProcessStrip from "../components/ProcessStrip";
import ConversionCTA from "../components/ConversionCTA";
import CalculatorCrossLink from "../components/CalculatorCrossLink";
import CrossStateGuides from "../components/CrossStateGuides";
import LazyYouTube from "../components/LazyYouTube";
import { Button } from "../components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "../components/ui/accordion";
import {
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
  MapPin,
  FileText,
  ExternalLink,
} from "lucide-react";
import { getStateGuides } from "../data/state_guides";

function ArticleNotFound({ basePath = "/", stateName = "" }) {
  return (
    <Layout>
      <SEO
        title="Guide not found | CarSaleDoc"
        description="The guide you are looking for does not exist."
        path="/"
        noindex
      />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-heading text-3xl font-bold">Guide not found</h1>
        <Link to={basePath} className="text-[var(--csd-primary)] underline mt-4 inline-block">
          Back to {stateName || "guides"}
        </Link>
      </div>
    </Layout>
  );
}

export default function ArticlePage({ stateSlug }) {
  const { slug } = useParams();
  const state = getStateGuides(stateSlug);

  if (!state) {
    return <ArticleNotFound />;
  }

  const article = state.getArticle(slug);
  if (!article) {
    return <ArticleNotFound basePath={state.basePath} stateName={state.stateName} />;
  }

  const path = `${state.basePath}/${article.slug}`;

  const faqJsonLd = article.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: article.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  // SEO enhancements (BreadcrumbList JSON-LD + dateModified + cross-state
  // internal linking) are purely additive — they don't remove or alter any
  // existing content. Enabled on all four state clusters; Google handles
  // new structured data and new internal links gracefully.
  const ENHANCED_STATES = new Set(["texas", "florida", "california", "new-york"]);
  const enhanced = ENHANCED_STATES.has(stateSlug);

  const articleJsonLd = enhanced
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.metaTitle,
        description: article.metaDescription,
        mainEntityOfPage: `https://www.carsaledoc.com${path}`,
        author: { "@type": "Organization", name: "CarSaleDoc", url: "https://www.carsaledoc.com" },
        publisher: {
          "@type": "Organization",
          name: "CarSaleDoc",
          logo: { "@type": "ImageObject", url: "https://www.carsaledoc.com/og-default.png" },
        },
        inLanguage: "en-US",
        about: { "@type": "Thing", name: `${state.stateName} private car sale` },
        datePublished: "2026-02-15",
        dateModified: "2026-06-18",
      }
    : {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.metaTitle,
        description: article.metaDescription,
        mainEntityOfPage: `https://www.carsaledoc.com${path}`,
        author: { "@type": "Organization", name: "CarSaleDoc" },
        publisher: { "@type": "Organization", name: "CarSaleDoc" },
        inLanguage: "en-US",
        about: { "@type": "Thing", name: `${state.stateName} private car sale` },
      };

  // BreadcrumbList — surfaces the "Home > State guide > Article" path on
  // SERPs instead of the raw URL. Only enabled for ENHANCED_STATES so we
  // don't change the HTML Google already indexed for the others.
  const breadcrumbJsonLd = enhanced
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.carsaledoc.com/" },
          { "@type": "ListItem", position: 2, name: `${state.stateName} car bill of sale`, item: `https://www.carsaledoc.com${state.basePath}` },
          { "@type": "ListItem", position: 3, name: article.title, item: `https://www.carsaledoc.com${path}` },
        ],
      }
    : null;

  const jsonLd = [articleJsonLd];
  if (breadcrumbJsonLd) jsonLd.push(breadcrumbJsonLd);
  if (faqJsonLd) jsonLd.push(faqJsonLd);

  const related = (article.related || [])
    .map((s) => state.guides.find((g) => g.slug === s))
    .filter(Boolean);

  return (
    <Layout>
      <SEO
        title={article.metaTitle}
        description={article.metaDescription}
        path={path}
        jsonLd={jsonLd}
      />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav
          className="text-xs text-[var(--csd-text-secondary)] mb-6 flex items-center gap-2"
          data-testid="article-breadcrumb"
        >
          <Link
            to={state.basePath}
            className="hover:text-[var(--csd-primary)] inline-flex items-center gap-1"
          >
            <ArrowLeft size={12} /> {state.stateName} car bill of sale
          </Link>
          <span>/</span>
          <span className="text-gray-700">Guide</span>
        </nav>

        <div
          className="inline-flex items-center gap-2 mb-4 text-xs uppercase tracking-widest font-semibold text-[var(--csd-primary)] bg-[var(--csd-primary-light)] px-3 py-1 rounded-full"
          data-testid="article-state-badge"
        >
          <MapPin size={12} /> {state.stateName} guide
        </div>

        <h1
          className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 leading-[1.1]"
          data-testid="article-h1"
        >
          {article.h1}
        </h1>

        <div
          className="mt-8 space-y-5 text-base sm:text-[17px] text-gray-800 leading-relaxed"
          data-testid="article-body"
        >
          {(() => {
            // Find the position right before the first <h2> so the hero
            // video sits AFTER the introductory paragraphs (SEO-friendly:
            // Google sees informational text first, then the video).
            const firstH2 = article.blocks.findIndex((b) => b.kind === "h2");
            const videoInsertIdx = firstH2 === -1 ? article.blocks.length : firstH2;
            const rendered = [];
            article.blocks.forEach((b, i) => {
              if (i === videoInsertIdx && article.heroVideo) {
                rendered.push(
                  <div key={`heroVideo-${article.slug}`} className="my-4" data-testid="article-hero-video">
                    <LazyYouTube
                      id={article.heroVideo.id}
                      title={article.heroVideo.title}
                      description={article.heroVideo.description}
                      uploadDate={article.heroVideo.uploadDate}
                      durationISO={article.heroVideo.durationISO || "PT2M"}
                      testId={`article-video-${article.slug}`}
                    />
                  </div>
                );
              }
              rendered.push((() => {
            if (b.kind === "h2") {
              return (
                <h2
                  key={i}
                  className="font-heading text-2xl font-bold tracking-tight pt-4 text-gray-900"
                >
                  {b.text}
                </h2>
              );
            }
            if (b.kind === "p") {
              return <p key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(b.html, { ALLOWED_TAGS: ["strong", "em", "a", "br", "code"], ALLOWED_ATTR: ["href", "rel", "target"] }) }} />;
            }
            if (b.kind === "list") {
              return (
                <ul key={i} className="space-y-2.5">
                  {b.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <CheckCircle2
                        size={18}
                        className="text-[var(--csd-primary)] mt-1 shrink-0"
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            if (b.kind === "callout") {
              return (
                <div
                  key={i}
                  className="rounded-2xl bg-[var(--csd-primary-light)]/40 border border-[var(--csd-primary-light)] p-5 text-[15px] text-[var(--csd-primary)] leading-relaxed font-medium"
                >
                  {b.text}
                </div>
              );
            }
            if (b.kind === "cta") {
              return (
                <div
                  key={i}
                  className="csd-card p-6 sm:p-8 my-6 bg-gradient-to-br from-white to-[var(--csd-primary-light)]/30 text-center"
                >
                  <div className="font-heading text-xl font-bold mb-2">
                    Ready to create your bill of sale?
                  </div>
                  <p className="text-sm text-[var(--csd-text-secondary)] mb-4">
                    Free to start. Pay only when you're ready to download and sign.
                  </p>
                  <Link to={state.cta.to} data-testid="article-cta">
                    <Button className="csd-btn-primary rounded-xl px-6 h-12 font-semibold">
                      {state.cta.label}
                    </Button>
                  </Link>
                </div>
              );
            }
            if (b.kind === "sources") {
              return (
                <aside
                  key={i}
                  className="rounded-2xl border border-[var(--csd-border)] bg-white p-5 mt-2"
                  data-testid="article-sources"
                >
                  <div className="text-xs uppercase tracking-widest font-semibold text-[var(--csd-text-secondary)] mb-3">
                    Official sources
                  </div>
                  <ul className="space-y-2">
                    {b.items.map((s, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <ExternalLink
                          size={14}
                          className="text-[var(--csd-primary)] mt-0.5 shrink-0"
                        />
                        <a
                          href={s.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[var(--csd-primary)] hover:underline break-words"
                        >
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </aside>
              );
            }
            return null;
          })());
            });
            // Edge case: heroVideo defined but no h2 blocks exist — append
            // at the end.
            if (article.heroVideo && firstH2 === -1) {
              // handled inside the loop via videoInsertIdx == blocks.length
            }
            return rendered;
          })()}
        </div>

        {/* FAQ */}
        {article.faqs?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-5">
              FAQ
            </h2>
            <Accordion
              type="single"
              collapsible
              className="csd-card divide-y divide-gray-200"
              data-testid="article-faq"
            >
              {article.faqs.map((f, i) => (
                <AccordionItem key={i} value={`q-${i}`} className="px-6 border-0">
                  <AccordionTrigger
                    className="font-semibold text-base hover:text-[var(--csd-primary)]"
                    data-testid={`article-faq-q-${i}`}
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--csd-text-secondary)] leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}
      </article>

      {/* Process strip — visual reinforcement after FAQ */}
      <ProcessStrip title={state.processTitle} lede={state.processLede} />

      {/* High-conversion CTA with testimonial + trust badges */}
      <ConversionCTA stateName={state.stateName} testId="article-final-cta" />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 pb-14">
        {/* Cross-state internal linking (TX + FL only). Pulls equity from
            the existing CA/NY pages into the new cluster without touching
            CA/NY content. */}
        {enhanced && (
          <CrossStateGuides
            currentStateSlug={stateSlug}
            currentArticleSlug={article.slug}
          />
        )}

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-5">
              More {state.stateName} guides
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`${state.basePath}/${r.slug}`}
                  className="csd-card p-5 group hover:border-[var(--csd-primary)] transition"
                >
                  <FileText className="text-[var(--csd-primary)] mb-3" size={20} />
                  <div className="font-heading font-bold mb-1">{r.title}</div>
                  <div className="text-sm text-[var(--csd-text-secondary)] mb-3">
                    {r.cardDescription}
                  </div>
                  <div className="text-sm text-[var(--csd-primary)] font-semibold inline-flex items-center gap-1">
                    Read guide <ChevronRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Disclaimer */}
        <div className="mt-12 text-xs text-[var(--csd-text-secondary)] leading-relaxed border-t border-[var(--csd-border)] pt-6">
          {state.disclaimer}
        </div>

        {/* Bottom back link */}
        <div className="mt-8">
          <Link
            to={state.basePath}
            className="text-sm text-[var(--csd-primary)] inline-flex items-center gap-1 hover:underline"
          >
            <ArrowLeft size={14} /> Back to {state.stateName} car bill of sale
          </Link>
        </div>
      </article>
    </Layout>
  );
}
